import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Plus, Pencil, Trash2, Check, GripVertical } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminCategoriasGastos() {
  const navigate = useNavigate();
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);

  useEffect(() => { loadCategorias(); }, []);

  const loadCategorias = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/categorias-gastos`);
      setCategorias(data);
    } catch {
      setError('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!nombre.trim()) { setError('El nombre es requerido'); return; }
    setSaving(true);
    setError('');
    try {
      if (editId) {
        await axios.put(`${API}/categorias-gastos/${editId}`, { nombre: nombre.trim(), descripcion: descripcion.trim() });
      } else {
        await axios.post(`${API}/categorias-gastos`, { nombre: nombre.trim(), descripcion: descripcion.trim() });
      }
      await loadCategorias();
      setSavedId(editId || '__new__');
      setTimeout(() => setSavedId(null), 1500);
      cancelForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta categoría?')) return;
    try {
      await axios.delete(`${API}/categorias-gastos/${id}`);
      await loadCategorias();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const startEdit = (cat) => {
    setEditId(cat.id);
    setNombre(cat.nombre);
    setDescripcion(cat.descripcion || '');
    setShowForm(true);
    setError('');
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditId(null);
    setNombre('');
    setDescripcion('');
    setError('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-zinc-900">Categorías de Gastos</h1>
            <p className="text-xs text-zinc-500">Catálogo para clasificar los gastos del corte</p>
          </div>
          <button
            onClick={() => { cancelForm(); setShowForm(true); }}
            className="flex items-center gap-1 bg-zinc-900 text-white px-3 py-2 rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Nueva
          </button>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
            <h3 className="font-bold text-zinc-800">{editId ? 'Editar categoría' : 'Nueva categoría'}</h3>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre de la categoría (ej. Gasolina, Limpieza...)"
              className="w-full h-11 px-4 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-zinc-600"
              autoFocus
            />
            <input
              type="text"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Descripción opcional"
              className="w-full h-11 px-4 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-zinc-600"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={saving || !nombre.trim()}
                className="flex-1 h-11 bg-zinc-900 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving
                  ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <><Check className="w-4 h-4" /> {editId ? 'Guardar cambios' : 'Crear categoría'}</>
                }
              </button>
              <button
                onClick={cancelForm}
                className="h-11 px-4 border border-zinc-300 rounded-lg text-sm text-zinc-600"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* List */}
        {categorias.length === 0 && !showForm ? (
          <div className="bg-white rounded-xl border border-zinc-200 p-8 text-center text-zinc-400">
            <p className="font-medium">Sin categorías configuradas</p>
            <p className="text-sm mt-1">Crea categorías para clasificar los gastos del corte de caja</p>
          </div>
        ) : (
          <div className="space-y-2">
            {categorias.map((cat, idx) => (
              <div
                key={cat.id}
                className="bg-white rounded-xl border border-zinc-200 px-4 py-3 flex items-center gap-3"
              >
                <GripVertical className="w-4 h-4 text-zinc-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-zinc-900">{cat.nombre}</p>
                  {cat.descripcion && <p className="text-xs text-zinc-500 truncate">{cat.descripcion}</p>}
                </div>
                <span className="text-xs text-zinc-400 shrink-0">#{idx + 1}</span>
                <button
                  onClick={() => startEdit(cat)}
                  className="w-9 h-9 flex items-center justify-center border border-zinc-200 rounded-lg text-zinc-500 hover:bg-zinc-50"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleEliminar(cat.id)}
                  className="w-9 h-9 flex items-center justify-center border border-red-200 rounded-lg text-red-500 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
